/** The router. */

import { lazy } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AuthLayout, BareLayout, MemberLayout, PrintLayout, PublicLayout } from './layouts'
import { AccountArea } from './AccountRail'
import { AppRoot } from './AppRoot'
import { RouteGuard, RootRedirect, type GuardKind } from './guards'

/* ---------- Public ------------------------------------------------------- */
const LandingPage = lazy(() => import('@/screens/public/LandingPage'))
const NewsListPage = lazy(() => import('@/screens/public/NewsListPage'))
const NewsArticlePage = lazy(() => import('@/screens/public/NewsArticlePage'))
const AdsPage = lazy(() => import('@/screens/public/AdsPage'))
const LinksPage = lazy(() => import('@/screens/public/LinksPage'))
const NotFoundPage = lazy(() => import('@/screens/public/NotFoundPage'))
const ForbiddenPage = lazy(() => import('@/screens/public/ForbiddenPage'))
const ContentPage = lazy(() => import('@/screens/public/ContentPage'))
const LandingMinimalPage = lazy(() => import('@/screens/public/LandingMinimalPage'))

/* ---------- Market ------------------------------------------------------- */
const MarketPricesPage = lazy(() => import('@/screens/market/MarketPricesPage'))
const MarketLmePage = lazy(() => import('@/screens/market/MarketLmePage'))
const MarketFxPage = lazy(() => import('@/screens/market/MarketFxPage'))
const MarketDashboardPage = lazy(() => import('@/screens/market/MarketDashboardPage'))
const MarketPriceIndexPage = lazy(() => import('@/screens/market/MarketPriceIndexPage'))
const MarketHistoryPage = lazy(() => import('@/screens/market/MarketHistoryPage'))
const MarketArchivePage = lazy(() => import('@/screens/market/MarketArchivePage'))

/* ---------- Entry and onboarding ----------------------------------------- */
const LoginPage = lazy(() => import('@/screens/onboarding/LoginPage'))
const RegisterWizardPage = lazy(() => import('@/screens/onboarding/RegisterWizardPage'))

/* ---------- Member ------------------------------------------------------- */
const DashboardPage = lazy(() => import('@/screens/dashboard/DashboardPage'))
const BoardPage = lazy(() => import('@/screens/board/BoardPage'))
const RfqBoardPage = lazy(() => import('@/screens/board/RfqBoardPage'))
const DealsBoardPage = lazy(() => import('@/screens/board/DealsBoardPage'))

const OfferWizardPage = lazy(() => import('@/screens/offers/OfferWizardPage'))
const BrandCreatePage = lazy(() => import('@/screens/offers/BrandCreatePage'))
const OfferDetailPage = lazy(() => import('@/screens/offers/OfferDetailPage'))
const OfferEditPage = lazy(() => import('@/screens/offers/OfferEditPage'))
const OfferCancelPage = lazy(() => import('@/screens/offers/OfferCancelPage'))
const MyOffersPage = lazy(() => import('@/screens/offers/MyOffersPage'))

const RfqWizardPage = lazy(() => import('@/screens/rfqs/RfqWizardPage'))
const RfqDetailPage = lazy(() => import('@/screens/rfqs/RfqDetailPage'))
const RfqEditPage = lazy(() => import('@/screens/rfqs/RfqEditPage'))
const RfqCancelPage = lazy(() => import('@/screens/rfqs/RfqCancelPage'))
const MyRfqsPage = lazy(() => import('@/screens/rfqs/MyRfqsPage'))

const BidPlacePage = lazy(() => import('@/screens/bids/BidPlacePage'))
const MyBidsPage = lazy(() => import('@/screens/bids/MyBidsPage'))
const BidHistoryPage = lazy(() => import('@/screens/bids/BidHistoryPage'))

const NegotiationPage = lazy(() => import('@/screens/deals/NegotiationPage'))
const DealsListPage = lazy(() => import('@/screens/deals/DealsListPage'))
const DealRoomPage = lazy(() => import('@/screens/deals/DealRoomPage'))
const DealDocumentsPage = lazy(() => import('@/screens/deals/DealDocumentsPage'))
const DealDeliveryPage = lazy(() => import('@/screens/deals/DealDeliveryPage'))
const DealNoticePrintPage = lazy(() => import('@/screens/deals/DealNoticePrintPage'))

const DocumentsLibraryPage = lazy(() => import('@/screens/account/DocumentsLibraryPage'))
const NotificationsPage = lazy(() => import('@/screens/account/NotificationsPage'))
const NotificationSettingsPage = lazy(() => import('@/screens/account/NotificationSettingsPage'))
const WalletPage = lazy(() => import('@/screens/account/WalletPage'))
const WalletTransactionsPage = lazy(() => import('@/screens/account/WalletTransactionsPage'))
const GradeReportPage = lazy(() => import('@/screens/account/GradeReportPage'))
const FavoritesPage = lazy(() => import('@/screens/account/FavoritesPage'))
const RequestsPage = lazy(() => import('@/screens/account/RequestsPage'))
const ClaimsPage = lazy(() => import('@/screens/account/ClaimsPage'))
const ClaimCreatePage = lazy(() => import('@/screens/account/ClaimCreatePage'))
const ClaimDetailPage = lazy(() => import('@/screens/account/ClaimDetailPage'))
const SettingsProfilePage = lazy(() => import('@/screens/account/SettingsProfilePage'))
const SettingsUsersPage = lazy(() => import('@/screens/account/SettingsUsersPage'))
const SettingsKycPage = lazy(() => import('@/screens/account/SettingsKycPage'))
const SettingsSecurityPage = lazy(() => import('@/screens/account/SettingsSecurityPage'))

/** Wrap an element in its guard. Every non public route names one. */
function g(kind: GuardKind, element: React.ReactNode, permission?: string) {
  return (
    <RouteGuard kind={kind} permission={permission}>
      {element}
    </RouteGuard>
  )
}

const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/',
        element: (
          <RootRedirect>
            <LandingPage />
          </RootRedirect>
        ),
      },
      { path: '/news', element: <NewsListPage /> },
      { path: '/news/:slug', element: <NewsArticlePage /> },
      { path: '/ads', element: <AdsPage /> },
      { path: '/links', element: <LinksPage /> },
      { path: '/pages/:slug', element: <ContentPage /> },
      { path: '/404', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },

  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterWizardPage /> },
      { path: '/register/:step', element: <RegisterWizardPage /> },
      { path: '/profile2', element: <RegisterWizardPage base="/profile2" wide /> },
      { path: '/profile2/:step', element: <RegisterWizardPage base="/profile2" wide /> },
    ],
  },

  {
    element: <MemberLayout />,
    children: [
      // Open to a file that is not trading yet:
      {
        element: <AccountArea />,
        children: [
          { path: '/dashboard', element: g('authenticated', <DashboardPage />) },
          { path: '/offers/mine', element: g('seller', <MyOffersPage />) },
          { path: '/rfqs/mine', element: g('buyer', <MyRfqsPage />) },
          { path: '/bids', element: g('member', <MyBidsPage />) },
          { path: '/deals', element: g('member', <DealsListPage />) },
          { path: '/documents', element: g('member', <DocumentsLibraryPage />) },
          { path: '/notifications', element: g('authenticated', <NotificationsPage />) },
          { path: '/favorites', element: g('member', <FavoritesPage />) },
          { path: '/requests', element: g('member', <RequestsPage />) },
          { path: '/wallet', element: g('member', <WalletPage />) },
          { path: '/wallet/transactions', element: g('member', <WalletTransactionsPage />) },
          { path: '/account/grade', element: g('member', <GradeReportPage />) },
          { path: '/claims', element: g('member', <ClaimsPage />) },
          { path: '/settings/profile', element: g('authenticated', <SettingsProfilePage />) },
          { path: '/settings/users', element: g('authenticated', <SettingsUsersPage />) },
          { path: '/settings/kyc', element: g('authenticated', <SettingsKycPage />) },
          { path: '/settings/notifications', element: g('authenticated', <NotificationSettingsPage />) },
          { path: '/settings/security', element: g('authenticated', <SettingsSecurityPage />) },
        ],
      },
      { path: '/board', element: g('authenticated', <BoardPage />) },
      { path: '/board/rfq', element: g('authenticated', <RfqBoardPage />) },
      { path: '/board/deals', element: g('authenticated', <DealsBoardPage />) },

      // The market screens sit inside the member shell, not the public one:
      { path: '/market', element: <Navigate to="/market/prices" replace /> },
      { path: '/market/prices', element: g('authenticated', <MarketPricesPage />) },
      { path: '/market/lme', element: g('authenticated', <MarketLmePage />) },
      { path: '/market/fx', element: g('authenticated', <MarketFxPage />) },
      { path: '/market/dashboard', element: g('authenticated', <MarketDashboardPage />) },
      { path: '/market/price-index', element: g('authenticated', <MarketPriceIndexPage />) },
      { path: '/market/history', element: g('authenticated', <MarketHistoryPage />) },
      { path: '/market/archive', element: g('authenticated', <MarketArchivePage />) },

      { path: '/offers/:offerId', element: g('authenticated', <OfferDetailPage />) },
      { path: '/offers/:offerId/edit', element: g('seller', <OfferEditPage />) },
      { path: '/offers/:offerId/cancel', element: g('seller', <OfferCancelPage />) },
      { path: '/offers/:offerId/bid', element: g('buyer', <BidPlacePage side="buy" />) },

      { path: '/rfqs/:rfqId', element: g('authenticated', <RfqDetailPage />) },
      { path: '/rfqs/:rfqId/edit', element: g('buyer', <RfqEditPage />) },
      { path: '/rfqs/:rfqId/cancel', element: g('buyer', <RfqCancelPage />) },
      { path: '/rfqs/:rfqId/bid', element: g('seller', <BidPlacePage side="sell" />) },

      { path: '/bids/:bidId/history', element: g('member', <BidHistoryPage />) },

      { path: '/negotiations/:negotiationId', element: g('member', <NegotiationPage />) },
      { path: '/deals/:dealId', element: g('member', <DealRoomPage />) },
      { path: '/deals/:dealId/documents', element: g('member', <DealDocumentsPage />) },
      { path: '/deals/:dealId/delivery', element: g('seller', <DealDeliveryPage />) },

      { path: '/claims/new', element: g('member', <ClaimCreatePage />) },
      { path: '/claims/:claimNo', element: g('member', <ClaimDetailPage />) },

      { path: '/settings', element: <Navigate to="/settings/profile" replace /> },

      { path: '/403', element: g('authenticated', <ForbiddenPage />) },
    ],
  },

  {
    element: <MemberLayout mode="focus" />,
    children: [
      { path: '/offers/new', element: <Navigate to="/offers/new/deal" replace /> },
      { path: '/offers/new/product/brand', element: g('seller', <BrandCreatePage />) },
      { path: '/offers/new/:step', element: g('seller', <OfferWizardPage />) },
      { path: '/rfqs/new', element: <Navigate to="/rfqs/new/deal" replace /> },
      { path: '/rfqs/new/:step', element: g('buyer', <RfqWizardPage />) },
    ],
  },

  {
    element: <BareLayout />,
    children: [{ path: '/landing/minimal', element: <LandingMinimalPage /> }],
  },

  {
    element: <PrintLayout />,
    children: [
      { path: '/deals/:dealId/notice', element: g('member', <DealNoticePrintPage />) },
    ],
  },
]

export const router = createBrowserRouter([{ element: <AppRoot />, children: routes }])
